import { Injectable } from '@angular/core';

import { createEffect, ofType, Actions } from '@ngrx/effects';
import { switchMap, map, tap, catchError } from 'rxjs/operators';

import * as fromNotfAlertStoreActions from './alert.actions';
import { AlertMiscDomainService } from '../../domain/alert-misc-domain.service';
import { Alert } from '../../domain/models';
import { NotfGravity, NotificationService, NotifType } from '@common/services';
import { of } from 'rxjs';

@Injectable()
export class NotfAlertEffects
{
    constructor (
        private actions: Actions,
        private alertMiscDomainService: AlertMiscDomainService,
        private notificationService: NotificationService
    )
    { }

    notfAlertLoadRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertLoadRequested),
                switchMap(() =>
                    this.alertMiscDomainService.getActiveAlerts()
                        .pipe(
                            map((alerts: Alert[]) =>
                                fromNotfAlertStoreActions.NotfAlertLoadSucceeded({ alerts })
                            )
                        )
                )
            )
    );

    createAlertRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfCreateAlertRequested),
                switchMap((alert) =>
                    this.alertMiscDomainService.createAlert(alert)
                        .pipe(
                            map((newAlert: Alert) =>
                                fromNotfAlertStoreActions.NotfCreateAlertSucceeded(newAlert)
                            ),
                            catchError(error =>
                                of(fromNotfAlertStoreActions.NotfCreateAlertFailed(error))
                            )
                        )
                )
            )
    );

    createAlertSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfCreateAlertSucceeded),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'alerte a bien été créé'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    createAlertFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfCreateAlertFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'opération a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    updateAlertRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfUpdateAlertRequested),
                switchMap((alert) =>
                    this.alertMiscDomainService.updateAlert(alert)
                        .pipe(
                            map((alertUpdated: Alert) =>
                                fromNotfAlertStoreActions.NotfUpdateAlertSucceeded(alertUpdated)
                            ),
                            catchError(error =>
                                of(fromNotfAlertStoreActions.NotfUpdateAlertFailed(error))
                            )
                        )
                )
            )
    );

    updateAlertSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfUpdateAlertSucceeded),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'alerte a bien été mise à jour'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    updateAlertFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfUpdateAlertFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'opération a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    alertsRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsRequested),
                switchMap(() =>
                    this.alertMiscDomainService.getAlerts()
                        .pipe(
                            map((alerts: Alert[]) =>
                                fromNotfAlertStoreActions.NotfAlertsSucceeded({ alerts })
                            ),
                            catchError(() =>
                                of(fromNotfAlertStoreActions.NotfAlertsFailed())
                            )
                        )
                )
            )
    );

    alertsFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'Le chargement des alertes a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch:false }
    );

    notfAlertsDisableRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDisableRequested),
                switchMap(({ alertId }) =>
                    this.alertMiscDomainService.disableAlert(alertId)
                        .pipe(
                            map(() =>
                                fromNotfAlertStoreActions.NotfAlertsDisableSucceeded({ alertId })
                            ),
                            catchError(() =>
                                of(fromNotfAlertStoreActions.NotfAlertsDisableFailed())
                            )
                        )
                )
            )
    );

    notfAlertsDisableSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDisableSucceeded),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'alerte a bien été activé'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    notfAlertsDisableFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDisableFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'La désactivation de l\'alerte a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch:false }
    );

    notfAlertsActivateRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsActivateRequested),
                switchMap(({ alertId }) =>
                    this.alertMiscDomainService.activateAlert(alertId)
                        .pipe(
                            map(() =>
                                fromNotfAlertStoreActions.NotfAlertsActivateSucceeded({ alertId })
                            ),
                            catchError(() =>
                                of(fromNotfAlertStoreActions.NotfAlertsActivateFailed())
                            )
                        )
                )
            )
    );

    notfAlertsActivateSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsActivateSucceeded),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'alerte a bien été activé'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    notfAlertsActivateFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsActivateFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'activation de l\'alerte a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch:false }
    );

    notfAlertsDeleteRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDeleteRequested),
                switchMap(({ alertId }) =>
                    this.alertMiscDomainService.deleteAlert(alertId)
                        .pipe(
                            map(() =>
                                fromNotfAlertStoreActions.NotfAlertsDeleteSucceeded({ alertId })
                            ),
                            catchError(() =>
                                of(fromNotfAlertStoreActions.NotfAlertsDeleteFailed())
                            )
                        )
                )
            )
    );

    notfAlertsDeleteSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDeleteSucceeded),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'alerte a bien été supprimé'
                        ],
                        NotfGravity.success,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch: false }
    );

    notfAlertsDeleteFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromNotfAlertStoreActions.NotfAlertsDeleteFailed),
                tap(() =>
                    this.notificationService.notify(
                        [
                            '',
                            'L\'opération a échoué'
                        ],
                        NotfGravity.danger,
                        NotifType.SNACKBAR
                    )
                )
            ),
    { dispatch:false }
    );
}
